import {AttrsSchema, Component} from 'panel';

import {TooltipItemType, TooltipSection} from 'common/components/chart-tooltip/types';
import {abbreviateNumber, capitalize, htmlEncodeString} from 'common/util';
import {getChartSegmentColorCombo} from 'common/util/colors';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {emitTrackEvent} from 'common/util/dom';
import {DEFAULT_CHART_HEIGHT} from 'common/report/constants';
import type {DateUnit, FormattedRow, RawCell, RetentionType, SelectedSegment} from 'common/report/util/retention/types';
import {
  formatNumber,
  formatPercent,
  getAddictionUnit,
  numberToRetentionBucketNumber,
} from 'common/report/util/retention/util';
import {AGGREGATED_ROW_NAME} from 'common/report/util/retention/constants';
import {hasRetentionData, mapToUiRows, RowWithAggregate} from 'common/report/data/retention/parsing';
import {REPORT_TYPES} from 'common/report/util/retention/constants';
import {ChartDisplayContext} from 'common/widgets/legend-line-chart';
import {ChartLegendTheme} from 'common/components/chart-legend/constants';
import {XAxisUnit} from 'common/components/line-chart/enums';

import template from './index.jade';
import './index.styl';

const MAX_X_TICKS = 20;

interface LegendSection {
  label: string;
  value: string;
  color: string;
  isDisabled: boolean;
  isSelected: boolean;
  wrapLabel: boolean;
  tooltip?: string;
}

interface LineChartRow extends FormattedRow<RawCell> {
  isHidden: boolean;
  color?: string;
}

interface LineChartState {
  hiddenSeries: Set<string>;
  chartRows: LineChartRow[];
}

interface LineChartAttrs {
  'chart-height': number;
  'data-group-name': string;
  'download-legend-series': string[];
  'download-mode': boolean;
  'display-context': ChartDisplayContext;
  'disable-empty-state': boolean;
  'has-aggregated-line': boolean;
  'is-cumulative': boolean;
  'is-unbounded': boolean;
  'is-download-truncated': boolean;
  'is-percentage-view': boolean;
  'is-segmented': boolean;
  'legend-theme': ChartLegendTheme;
  'project-timezone': string;
  'project-today': string;
  'reflow-interval': number;
  'retention-type': RetentionType;
  'selected-segments': SelectedSegment[];
  'show-legend': boolean;
  unit: DateUnit;
}

export class RetentionLineChart extends Component<LineChartState, LineChartAttrs> {
  private _results: RowWithAggregate[] = [];
  static get attrsSchema(): AttrsSchema<LineChartAttrs> {
    return {
      'chart-height': `number`,
      'data-group-name': {type: `string`, default: `user`},
      'display-context': {type: `string`, default: ChartDisplayContext.Primary},
      'disable-empty-state': `boolean`,
      'download-legend-series': `json`,
      'download-mode': `boolean`,
      'has-aggregated-line': `boolean`,
      'is-cumulative': `boolean`,
      'is-unbounded': `boolean`,
      'is-download-truncated': `boolean`,
      'is-percentage-view': `boolean`,
      'is-segmented': `boolean`,
      'legend-theme': {type: `string`, default: ChartLegendTheme.Primary},
      'project-timezone': `string`,
      'project-today': `string`,
      'reflow-interval': {
        type: `number`,
        default: null,
        description: `sets an interval for how often the line chart is redrawn and updated - only used in dashboard card for now`,
      },
      'retention-type': `string`,
      'selected-segments': `json`,
      'show-legend': `boolean`,
      unit: {type: `string`, default: `day`, enum: [`day`, `week`, `month`]},
    };
  }

  get config() {
    return {
      defaultState: {
        hiddenSeries: new Set([]),
        chartRows: [],
      },
      helpers: {
        DEFAULT_CHART_HEIGHT,
        getDataGroupName: () => capitalize(this.attr(`data-group-name`)),
        hasResults: () => hasRetentionData(this._results),
        getChartData: () => {
          return this.state.chartRows
            .filter((row) => !row.isHidden)
            .map((row) => {
              const data = row.data.map((cell: any, idx) => [
                idx,
                this.attr(`is-percentage-view`) ? cell.percent : cell.absolute_value,
              ]);
              const incompleteLength = this.getChartRowIncompleteLength(row);
              return {
                name: row.raw_head,
                data,
                color: row.color,
                incompleteLength,
              };
            });
        },
        isDisplayedAsDashboardCard: () => this.attr(`display-context`) === ChartDisplayContext.DashboardCard,
        getChartNoteLabel: () => this.getChartDescriptionNoteLabel(),
        getLegendSections: () => {
          if (!this.helpers.hasResults() || !this.attr(`show-legend`)) {
            return JSON.stringify([]);
          }

          let chartRowsNames = this.state.chartRows.map((row) => row.raw_head);
          if (this.helpers.isDisplayedAsDashboardCard()) {
            chartRowsNames = this.selectedSegmentsRows.map((row) => row.raw_head);
          }
          const items: LegendSection[] = chartRowsNames.map((name) => ({
            label: name,
            value: name,
            color: this.state.chartRows.find((row) => row.raw_head === name).color,
            isDisabled: false,
            isSelected: !this.state.hiddenSeries.has(name),
            tooltip: null,
            wrapLabel: true,
          }));

          return JSON.stringify([{items}]);
        },
        getFormatTooltip: (x: number, _y: number, _series: any, idx: number) => {
          const row = this.selectedSegmentsRows[idx];
          const cell = row.data[x];

          const sections = this.getTooltipSections(row, x);

          const footerText = cell.incomplete ? `footer-text="Incomplete ${this.attr(`unit`)}" ` : ``;

          return `<mp-chart-tooltip title-color=${row.color} ${footerText} sections="${htmlEncodeString(
            JSON.stringify(sections),
          )}">`;
        },

        getXAxisOptions: () => {
          return {unit: XAxisUnit.Time};
        },

        isEndDateIncomplete: () => {
          const selectedSegmentsRows = this.selectedSegmentsRows;
          if (selectedSegmentsRows.every((row) => row.data.length < 2)) {
            return false;
          }
          for (const row of selectedSegmentsRows) {
            for (const cell of row.data) {
              if (cell.incomplete) {
                return true;
              }
            }
          }
          return false;
        },

        formatXAxisLabel: (value: number) => {
          const isAddictionReport = this.attr(`retention-type`) === REPORT_TYPES.ADDICTION;
          const labelNumber = isAddictionReport ? value + 2 : value === 0 ? `<1` : numberToRetentionBucketNumber(value);
          const unit = isAddictionReport ? `${getAddictionUnit(this.attr(`unit`))}s` : this.attr(`unit`);
          return `${labelNumber} ${unit}`;
        },
        formatYAxisLabel: (value: number, precision = 0) => {
          if (this.attr(`is-percentage-view`)) {
            return formatPercent(value, precision);
          } else {
            return value >= 10000 ? abbreviateNumber(value) : value;
          }
        },
        formatTicksX: (min: number, max: number) => {
          if (this.helpers.isDisplayedAsDashboardCard()) {
            return undefined;
          }
          const xTicks = [min];
          const skip = max - min + 1 > MAX_X_TICKS ? Math.floor((max - min + 1) / MAX_X_TICKS) : 0;
          while (min <= max) {
            if (min > xTicks[xTicks.length - 1] + skip) {
              xTicks.push(min);
            }
            min += 1;
          }
          return xTicks;
        },
        handleCheckboxToggled: (ev) => {
          let selectedSegments: SelectedSegment[];
          let itemName: string;
          let isDeselect = false;

          if (ev.detail.isSelectAll) {
            itemName = `Select All`;
            if (this.state.hiddenSeries.size === 0) {
              selectedSegments = [];
              isDeselect = true;
            } else {
              selectedSegments = this.state.chartRows.map((row, idx) => ({
                name: row.raw_head,
                color: getChartSegmentColorCombo(idx).primary,
              }));
            }
          } else {
            itemName = ev.detail.item.value;
            selectedSegments = this.attr(`selected-segments`);
            if (this.state.hiddenSeries.has(itemName)) {
              selectedSegments = [
                ...selectedSegments,
                {name: itemName, color: getChartSegmentColorCombo(selectedSegments.length + 1).primary},
              ];
            } else {
              selectedSegments = selectedSegments.filter((element) => element.name !== itemName);
            }
          }
          this.trackChartLegend(`Clicked chart legend`, {
            'is deselect': isDeselect,
            'is average row': itemName === AGGREGATED_ROW_NAME(this.attr(`retention-type`)),
            'clicked element': itemName,
          });
          this.dispatchEvent(
            new CustomEvent(`update`, {
              bubbles: true,
              detail: {
                selectedSegments,
              },
            }),
          );
        },
      },
      template,
    };
  }

  attributeChangedCallback(attr, oldValue, newValue) {
    super.attributeChangedCallback(attr, oldValue, newValue);
    if (attr === `selected-segments` && this._results.length > 0) {
      this.updateChartRows(this.attr(`selected-segments`), false);
    } else if (attr === `has-aggregated-line` && this._results.length > 0) {
      this.updateChartRows(this.attr(`selected-segments`));
    }
  }

  getChartRowIncompleteLength(row) {
    const incompleteIdx = row.data.findIndex((data) => data.incomplete);
    return incompleteIdx >= 0 ? row.data.length - incompleteIdx : 0;
  }

  getTooltipSections(row, x): TooltipSection[] {
    const isAggregatedRow = this.isAggregateRow(row);
    const cell = row.data[x];

    const {subtitle, titleSection} = this.getTooltipDetail(row, x);

    const value = isAggregatedRow
      ? `${formatNumber(cell.absolute_value)}/${formatNumber(cell.cohort_size)}`
      : formatNumber(cell.absolute_value);
    return [
      {
        rows: [
          {
            items: titleSection,
          },
          {
            items: [{text: subtitle, type: TooltipItemType.Detail}],
          },
        ],
      },
      {
        rows: [
          {
            items: [
              {text: cell.percent_value, type: TooltipItemType.Data},
              {
                text: this.attr(`retention-type`) === `addiction` ? `engaged` : `retention`,
                type: TooltipItemType.Text,
              },
            ],
          },
          {
            items: [
              {text: value, type: TooltipItemType.Data},
              {
                text: isAggregatedRow
                  ? `${this.attr(`data-group-name`)} instances`
                  : `${this.attr(`data-group-name`)}s`,
                type: TooltipItemType.Text,
              },
            ],
          },
        ],
      },
    ];
  }
  getTooltipDetail(row, x) {
    const unit = this.attr(`unit`);
    const cell = row.data[x];
    const isAggregatedRow = this.isAggregateRow(row);

    let title = ``;
    if (this.attr(`is-segmented`)) {
      title = row.raw_head;
    } else {
      title = isAggregatedRow
        ? `${AGGREGATED_ROW_NAME(this.attr(`retention-type`))}`
        : `Users entered on ${cell.entered_value}`;
    }

    const subtitle =
      this.attr(`retention-type`) === REPORT_TYPES.ADDICTION
        ? `${this.attr(`is-cumulative`) ? `Did [A] at least` : `In minimum`} ${x + 2} ${getAddictionUnit(
            unit,
          )} in a ${unit}`
        : `Retained ${this.attr(`is-unbounded`) ? `on and after` : `on`} ${numberToRetentionBucketNumber(x)} ${unit}`;

    const titleSection = [{text: title, type: TooltipItemType.Strong}];
    if (isAggregatedRow) {
      titleSection.push({text: cell.incomplete ? `` : `in completed intervals`, type: TooltipItemType.Text});
    }

    return {subtitle, titleSection};
  }
  isAggregateRow(row) {
    return !this.attr(`is-segmented`) && row.display_head === AGGREGATED_ROW_NAME(this.attr(`retention-type`));
  }

  get selectedSegmentsRows() {
    return this.state.chartRows.filter((row) => !row.isHidden);
  }

  /**
   * Determine which segments not to show in the line-chart.
   *
   * There is special logic for download-mode here.
   */

  getUnselectedSegments(selectedSegments: SelectedSegment[], formattedRows: Array<FormattedRow<RawCell>>): Set<string> {
    const allSegments = formattedRows.map((r) => r.raw_head);

    let visibleSegments: string[];
    if (this.attr(`download-mode`)) {
      // TODO: get the selectedSegments into the URL so we don't need
      // a special path for download-legend-series
      const specialDownloadSegments = this.attr(`download-legend-series`);
      if (specialDownloadSegments?.length > 0) {
        visibleSegments = specialDownloadSegments;
      } else {
        visibleSegments = selectedSegments.map((s) => s.name);
      }
    } else {
      visibleSegments = selectedSegments.map((s) => s.name);
    }

    return new Set(allSegments.filter((s) => !visibleSegments.includes(s)));
  }

  updateChartRows(selectedSegments: SelectedSegment[], deepUpdate = true) {
    const formattedRows = deepUpdate
      ? mapToUiRows(
          this._results,
          {
            percentageView: this.attr(`is-percentage-view`),
            dateUnit: this.attr(`unit`),
            retentionType: this.attr(`retention-type`),
          },
          new Date(this.attr(`project-today`)),
          this.attr(`project-timezone`),
        )
      : this.state.chartRows;

    const hiddenSeries = this.getUnselectedSegments(selectedSegments, formattedRows);
    const chartRows = formattedRows.map((row, idx) => ({
      ...row,
      // prioritize color assigned by main app, then fall back to self-assigned color
      color:
        selectedSegments.find((segment) => segment.name === row.raw_head)?.color ||
        getChartSegmentColorCombo(idx).primary,
      isHidden: hiddenSeries.has(row.raw_head),
    }));
    this.update({chartRows, hiddenSeries});
  }

  set results(results: RowWithAggregate[]) {
    if (results) {
      this._results = results;
      if (hasRetentionData(this._results)) {
        this.updateChartRows(this.attr(`selected-segments`));
      }
    }
  }

  getChartDescriptionNoteLabel() {
    const dateUnit = this.attr(`unit`);
    const dataGroupName = `${this.attr(`data-group-name`)}s`;
    const retentionType = this.attr(`retention-type`);

    let description;
    if (retentionType === `addiction`) {
      if (!this.attr(`is-cumulative`)) {
        description = `The number of ${dateUnit === `day` ? `hours` : `days`} in a ${dateUnit} ${dataGroupName} did A`;
      } else {
        description = `${capitalize(dataGroupName)} did A at least X ${
          dateUnit === `day` ? `hours` : `days`
        } in a ${dateUnit}`;
      }
    } else {
      if (this.attr(`is-unbounded`)) {
        description = `X and more ${dateUnit}s later your ${dataGroupName} came back and did B.`;
      } else {
        description = `The number of exact ${dateUnit}s later your ${dataGroupName} came back and did B.`;
      }
    }
    return description;
  }

  trackChartLegend(eventName, properties = {}) {
    emitTrackEvent(this, {
      eventName,
      eventPrefix: `Chart Legend`,
      props: {
        report: `retention`,
        ...properties,
      },
    });
  }
}

export const RetentionLineChartEl = defineAndMakeJsxFactory(`mp-retention-line-chart`, RetentionLineChart);
